import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * MonthlyReportCard
 * Displays the monthly "Nervous System Wrapped" — a GPT-4o mentor letter.
 * Generates on demand if no report exists. Auto-generated monthly via cron.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VALENCE = {
  shadow: { bg: 'rgba(201,152,62,0.07)', border: '#C9983E', label: '#C9983E' },
  breakthrough: { bg: 'rgba(30,107,104,0.07)', border: '#2D8B87', label: '#1E6B68' },
  pattern: { bg: 'rgba(184,211,207,0.12)', border: '#B8D3CF', label: '#6A8F8C' },
};

export default function MonthlyReportCard({ userId }) {
  const [state, setState] = useState('idle'); // idle | loading | ready | generating | error
  const [report, setReport] = useState(null);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (userId) loadLatest();
  }, [userId]); // eslint-disable-line

  const loadLatest = async () => {
    setState('loading');
    try {
      const res = await axios.get(`${API}/training/monthly-report/${userId}/latest`);
      if (res.data.exists) {
        setReport(res.data.report);
        setStats(res.data.stats);
        setState('ready');
      } else {
        setState('idle');
      }
    } catch {
      setState('idle');
    }
  };

  const generate = async () => {
    setState('generating');
    try {
      const res = await axios.post(`${API}/training/monthly-report/${userId}/generate`);
      if (res.data.generated) {
        setReport(res.data.report);
        setStats(res.data.stats);
        setState('ready');
        setOpen(true);
        toast.success('Your monthly report is ready');
      }
    } catch (e) {
      setState('error');
      const detail = e?.response?.data?.detail || 'Could not generate report. Try again later.';
      toast.error(detail);
    }
  };

  const eriDelta = stats?.eri_delta;
  const sessions = stats?.total_sessions;
  const monthLabel = report?.month;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)',
      borderRadius: '20px',
      border: '1px solid rgba(184,211,207,0.3)',
      overflow: 'hidden',
      boxShadow: '0 2px 20px rgba(30,107,104,0.07)',
      marginBottom: '16px',
    }}>

      {/* Header row — div, not button: it contains the Generate button and
          nesting buttons is invalid HTML (React logs hydration errors). */}
      <div
        onClick={() => state === 'ready' && setOpen(o => !o)}
        role={state === 'ready' ? 'button' : undefined}
        tabIndex={state === 'ready' ? 0 : undefined}
        onKeyDown={(e) => { if (state === 'ready' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(o => !o); } }}
        style={{
          width: '100%', background: 'none', border: 'none',
          padding: '18px 20px', cursor: state === 'ready' ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', gap: '14px', textAlign: 'left',
          boxSizing: 'border-box',
        }}
      >
        {/* Calendar icon */}
        <div style={{
          width: '40px', height: '40px', borderRadius: '11px', flexShrink: 0,
          background: 'linear-gradient(135deg, rgba(30,107,104,0.12), rgba(45,139,135,0.08))',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <rect x="3" y="4" width="14" height="13" rx="2.5" stroke="#1E6B68" strokeWidth="1.4"/>
            <path d="M3 8h14" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round"/>
            <path d="M7 2v3M13 2v3" stroke="#1E6B68" strokeWidth="1.4" strokeLinecap="round"/>
            <circle cx="7.5" cy="12" r="1" fill="#1E6B68" opacity="0.5"/>
            <circle cx="10" cy="12" r="1" fill="#1E6B68" opacity="0.5"/>
            <circle cx="12.5" cy="12" r="1" fill="#1E6B68" opacity="0.5"/>
          </svg>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <p style={{ margin: 0, fontWeight: '700', fontSize: '0.9rem', color: '#1E3A3A' }}>
              {monthLabel ? `${monthLabel} Report` : 'Monthly Report'}
            </p>
            {eriDelta != null && (
              <span style={{
                fontSize: '0.68rem', fontWeight: '700', padding: '2px 7px', borderRadius: '10px',
                background: eriDelta >= 0 ? 'rgba(30,107,104,0.1)' : 'rgba(201,152,62,0.1)',
                color: eriDelta >= 0 ? '#1E6B68' : '#C9983E',
              }}>
                ERI {eriDelta >= 0 ? '+' : ''}{eriDelta?.toFixed(1)}
              </span>
            )}
          </div>
          <p style={{ margin: '2px 0 0', fontSize: '0.75rem', color: '#9AADAB' }}>
            {state === 'loading' ? 'Checking…' :
             state === 'generating' ? 'Writing your report…' :
             state === 'ready' ? `${sessions} sessions · tap to read` :
             state === 'error' ? 'Generation failed' :
             'Your nervous system, written.'}
          </p>
        </div>

        {state === 'ready' && (
          <span style={{ color: '#B8D3CF', fontSize: '0.75rem', flexShrink: 0 }}>
            {open ? '▲' : '▼'}
          </span>
        )}
        {(state === 'idle' || state === 'error') && (
          <button
            onClick={(e) => { e.stopPropagation(); generate(); }}
            style={{
              background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
              border: 'none', borderRadius: '10px', padding: '8px 14px',
              color: '#fff', fontSize: '0.75rem', fontWeight: '600',
              cursor: 'pointer', flexShrink: 0,
            }}
          >
            Generate
          </button>
        )}
      </div>

      {/* Generating spinner */}
      {state === 'generating' && (
        <div style={{ padding: '4px 20px 18px', display: 'flex', gap: '5px', alignItems: 'center' }}>
          {[0, 0.2, 0.4].map((d, i) => (
            <div key={i} style={{
              width: '5px', height: '5px', borderRadius: '50%', background: '#2D8B87',
              animation: `mrDot 1.2s ease-in-out ${d}s infinite`,
            }} />
          ))}
          <style>{`@keyframes mrDot{0%,100%{opacity:.3;transform:scale(.8)}50%{opacity:1;transform:scale(1)}}`}</style>
          <span style={{ fontSize: '0.78rem', color: '#9AADAB', marginLeft: '6px' }}>Writing your mentor letter…</span>
        </div>
      )}

      {/* Report body */}
      {state === 'ready' && open && report && (
        <div style={{ padding: '0 20px 24px', borderTop: '1px solid rgba(184,211,207,0.2)' }}>

          {/* Opening */}
          <p style={{
            marginTop: '20px', fontSize: '0.9rem', color: '#3A5250',
            lineHeight: 1.75, fontStyle: 'italic',
          }}>
            {report.opening}
          </p>

          {/* Pattern cards */}
          {(report.patterns || []).map((p, i) => (
            <div key={i} style={{
              ...VALENCE.pattern,
              background: VALENCE.pattern.bg,
              borderLeft: `3px solid ${VALENCE.pattern.border}`,
              borderRadius: '12px', padding: '14px 16px', marginTop: '12px',
            }}>
              <p style={{ margin: '0 0 5px', fontSize: '0.68rem', fontWeight: '700', color: VALENCE.pattern.label, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Pattern</p>
              <p style={{ margin: '0 0 6px', fontWeight: '700', fontSize: '0.88rem', color: '#1E3A3A' }}>{p.title}</p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#555', lineHeight: 1.6 }}>{p.body}</p>
            </div>
          ))}

          {/* Shadow zone */}
          {report.shadow_zone && (
            <div style={{
              background: VALENCE.shadow.bg,
              borderLeft: `3px solid ${VALENCE.shadow.border}`,
              borderRadius: '12px', padding: '14px 16px', marginTop: '12px',
            }}>
              <p style={{ margin: '0 0 5px', fontSize: '0.68rem', fontWeight: '700', color: VALENCE.shadow.label, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Shadow Zone</p>
              <p style={{ margin: '0 0 6px', fontWeight: '700', fontSize: '0.88rem', color: '#1E3A3A' }}>{report.shadow_zone.title}</p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#555', lineHeight: 1.6 }}>{report.shadow_zone.body}</p>
            </div>
          )}

          {/* Breakthrough */}
          {report.breakthrough && (
            <div style={{
              background: VALENCE.breakthrough.bg,
              borderLeft: `3px solid ${VALENCE.breakthrough.border}`,
              borderRadius: '12px', padding: '14px 16px', marginTop: '12px',
            }}>
              <p style={{ margin: '0 0 5px', fontSize: '0.68rem', fontWeight: '700', color: VALENCE.breakthrough.label, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Breakthrough</p>
              <p style={{ margin: '0 0 6px', fontWeight: '700', fontSize: '0.88rem', color: '#1E3A3A' }}>{report.breakthrough.title}</p>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#555', lineHeight: 1.6 }}>{report.breakthrough.body}</p>
            </div>
          )}

          {/* Intention */}
          {report.intention && (
            <p style={{
              marginTop: '20px', fontSize: '0.88rem', color: '#1E6B68',
              fontStyle: 'italic', textAlign: 'center', lineHeight: 1.7,
              padding: '0 8px',
            }}>
              "{report.intention}"
            </p>
          )}

          {/* Closing */}
          {report.closing && (
            <p style={{
              marginTop: '16px',
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.05rem', color: '#1E3A3A',
              textAlign: 'center', lineHeight: 1.6,
            }}>
              {report.closing}
            </p>
          )}

          {/* Regenerate link */}
          <button
            onClick={() => { setReport(null); setState('idle'); }}
            style={{
              marginTop: '16px', background: 'none', border: 'none',
              color: '#B8D3CF', fontSize: '0.72rem', cursor: 'pointer', padding: '4px 0',
            }}
          >
            ↺ Regenerate
          </button>
        </div>
      )}
    </div>
  );
}
